<template>
  <div class="app-shell" :class="isDarkTheme ? 'shell--dark' : 'shell--light'">
    <AppNavbar @toggle-sidebar="toggleSidebar" />
    <div class="app-shell__body">
      <AppSidebar :minimized="isSidebarMinimized" />
      <main
        class="app-shell__main"
        :class="{ 'app-shell__main--flat-page': $route.meta && $route.meta.flatBackground }"
      >
        <div class="app-shell__scroll">
          <RouterView :key="$route.meta.stableKey ? $route.path : $route.fullPath" />
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, onMounted } from 'vue'
import { useStore } from 'vuex'
import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'

const store = useStore()
const isSidebarMinimized = computed(() => store.state.isSidebarMinimized)

const toggleSidebar = () => {
  store.commit('toggleSidebar')
}


// Theme sync
const currentTheme = ref('light')

function syncTheme() {
  try {
    if (typeof window !== 'undefined' && window.getAppTheme) {
      currentTheme.value = window.getAppTheme() ?? 'light'
    }
  } catch (e) {}
}

const isDarkTheme = computed(() => currentTheme.value === 'dark')

// Mobile viewport scale
const onResize = () => {
  const isMobile = window.innerWidth <= 640
  const scale = isMobile ? 0.5 : 1
  let meta = document.querySelector('meta[name="viewport"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('name', 'viewport')
    document.head.appendChild(meta)
  }
  meta.content = `width=device-width, initial-scale=${scale}, maximum-scale=${scale}, user-scalable=no`
}

onMounted(() => {
  syncTheme()
  window.addEventListener('storage', syncTheme)
  window.addEventListener('app-theme-changed', syncTheme)
  window.addEventListener('resize', onResize)
  onResize()
})

onBeforeUnmount(() => {
  window.removeEventListener('storage', syncTheme)
  window.removeEventListener('app-theme-changed', syncTheme)
  window.removeEventListener('resize', onResize)
})
</script>

<style lang="scss">
.app-shell {
  height: 100vh;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: hidden;
  box-sizing: border-box;

  // ── Tokens dark (cinzas neutros, sem azul/roxo) ─────────────
  &.shell--dark {
    background: #1e1e1e;
    --shell-glass-bg: rgba(28, 28, 28, 0.97);
    --shell-glass-border: rgba(255, 255, 255, 0.06);
    --shell-text: #e8e8e8;
    --shell-text-muted: rgba(232, 232, 232, 0.45);
    --shell-item-hover: rgba(255, 255, 255, 0.06);
    --shell-item-active-bg: #ff4d2e;
    --shell-item-active-color: #fff;
    --shell-scrollbar: rgba(255, 255, 255, 0.1);
    --shell-section-label: rgba(232, 232, 232, 0.4);
    --shell-input-bg: rgba(255, 255, 255, 0.05);
    --shell-input-border: rgba(255, 255, 255, 0.09);
    --shell-main-bg: #2c2c2c;
    // superfície de cards/painéis e apoios (usados p.ex. na Auditoria do Slack)
    --shell-surface: #2c2c2c;
    --shell-text-soft: rgba(232, 232, 232, 0.55);
    --shell-border: rgba(255, 255, 255, 0.08);
  }

  // ── Tokens light ─────────────────────────────────────────────
  &.shell--light {
    background: #eef0f5;
    --shell-glass-bg: rgba(255, 255, 255, 0.9);
    --shell-glass-border: rgba(0, 0, 0, 0.08);
    --shell-text: #15171d;
    --shell-text-muted: rgba(21, 23, 29, 0.45);
    --shell-item-hover: rgba(0, 0, 0, 0.04);
    --shell-item-active-bg: #ff4d2e;
    --shell-item-active-color: #fff;
    --shell-scrollbar: rgba(0, 0, 0, 0.1);
    --shell-section-label: rgba(21, 23, 29, 0.4);
    --shell-input-bg: rgba(0, 0, 0, 0.04);
    --shell-input-border: rgba(0, 0, 0, 0.1);
    --shell-main-bg: rgba(255, 255, 255, 0.9);
    --shell-surface: #ffffff;
    --shell-text-soft: #6b7280;
    --shell-border: rgba(0, 0, 0, 0.08);
  }

  // ── Body ──────────────────────────────────────────────────────
  &__body {
    display: flex;
    flex: 1;
    gap: 14px;
    overflow: hidden;
    min-height: 0;
  }

  // ── Main content ──────────────────────────────────────────────
  &__main {
    flex: 1;
    border-radius: 14px;
    overflow: hidden;
    background: var(--shell-main-bg);
    border: 1px solid var(--shell-glass-border);
    position: relative;

    @supports (backdrop-filter: blur()) {
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
    }

    // Painéis (Indicadores, Monitoring): fundo opaco e sem blur. São dezenas de
    // cartões numa tela só, e o backdrop-filter do shell por baixo deles custa
    // caro a cada scroll. A COR vem do tema — era fixa em cinza-escuro, o que
    // deixava as duas telas escuras mesmo para quem usa o app no claro.
    &--flat-page {
      background: var(--panel-page-bg) !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }
  }

  &__scroll {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    color: var(--shell-text);
    padding: 20px 24px;
    box-sizing: border-box;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: var(--shell-scrollbar);
      border-radius: 2px;
    }
  }

  // Painéis: padding menor para não cortar as bordas arredondadas dos cartões.
  &__main--flat-page &__scroll {
    padding: 16px 20px;
  }
}

// ── Adaptações globais de tema para componentes internos ──────

// Tema light: cards e tabelas
html.theme-light {
  // Força fundo claro nos cards das páginas
  .va-card {
    background: #ffffff !important;
    color: #15171d !important;
  }

  // Os painéis têm superfície própria (ver panel-theme.css) — no claro ela
  // coincide com o branco acima, mas quem manda é o token.
  .monitoring-page .va-card,
  .indicators-page .va-card {
    background: var(--panel-surface) !important;
    color: var(--panel-text) !important;
  }

  // Contador de registros (ex: 282 CDs) — adapta para tema light
  .count-indicator,
  [class*="count-card"],
  [class*="counter-card"] {
    background: #ffffff !important;
    color: #15171d !important;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  }
}

// Tema dark: garante que tabelas e cards usem o fundo correto
html.theme-dark {
  .va-card {
    background: #2c2c2c !important;
  }

  // Os painéis têm superfície própria (ver panel-theme.css), um pouco mais
  // clara que a do shell para destacar o cartão do fundo.
  .monitoring-page .va-card,
  .indicators-page .va-card {
    background: var(--panel-surface) !important;
    color: var(--panel-text) !important;
  }
}
</style>
